import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { HtmlContent } from '~/App/shared/components/HtmlContent';
import { slideElementDown, slideElementUp } from '~/App/helpers/animations';
import mq from '~/App/helpers/mq';
import { angleDown } from '~/App/helpers/icons';
import { heading4, description2 } from '~/App/helpers/mixins';

type WrapperProps = {
  $isEnabled: boolean;
};

type DescriptionProps = {
  $isEnabled?: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  border-bottom-width: ${({ $isEnabled }) => ($isEnabled ? '1px' : '0px')};
  border-bottom-style: solid;
  border-bottom-color: ${({ theme }) => theme.themeColors.primary};
`;

const Description = styled.div<DescriptionProps>`
  ${description2};
  overflow: hidden;
  height: ${({ $isEnabled }) => ($isEnabled ? '0' : 'auto')};
  transition: height 300ms ease-in-out;
  padding-right: 0;
  padding-bottom: 0;
`;

const Summary = styled.summary`
  position: relative;
  ${heading4}
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: 1.555555556;
  color: ${({ theme }) => theme.themeColors.primary};
  padding: 0 2.5rem 0.5rem 0;
  margin: 1rem 0 0 0;
  list-style-type: none;

  ${mq('≥small')`
    line-height: 1.555555556;
  `};

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.themeColors.primaryHover};

    &::after {
      background-color: ${({ theme }) => theme.themeColors.primaryHover};
    }
  }

  &::after {
    ${angleDown};
    font-size: 1.75rem;
    line-height: 1.75rem;
    display: block;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.themeColors.primary};
    text-align: center;
    position: absolute;
    right: 0;
    top: 0;
    color: ${({ theme }) => theme.colors.white};
    transition: transform 0.3s ease;
    transform: none;
  }
`;

const Details = styled.details`
  &[open] {
    & ${Description} {
      padding-right: 2.5rem;
      padding-bottom: 1rem;
    }

    & ${Summary} {
      font-weight: ${({ theme }) => theme.fontWeights.bold};

      &::after {
        transform: rotate(-180deg);
      }
    }
  }
`;

const Heading4 = styled.h4`
  ${heading4};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.themeColors.primary};
  padding: 0 0 0.5rem 0;
  line-height: 1.555555556;
  margin: 1rem 0 0 0;

  ${mq('≥small')`
    line-height: 1.555555556;
  `};
`;

interface Props {
  isEnabled: boolean;
  heading: string;
  content: string;
}

export function Accordion(props: Props) {
  const { isEnabled, heading, content } = props;
  const ref = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const elem = ref.current;
    const contentElem = elem?.querySelector('.content') as HTMLElement;

    const slideToggle = () =>
      setTimeout(() => {
        const duration = 300;

        if (elem?.open) {
          slideElementDown(contentElem, duration);
        } else {
          slideElementUp(contentElem, duration);
        }
      }, 50);

    const cleanUp = () => {
      contentElem.style.height = 'auto';
    };

    if (elem && contentElem) {
      elem.addEventListener('toggle', slideToggle);
      contentElem.addEventListener('transitionend', cleanUp);

      return () => {
        elem.removeEventListener('toggle', slideToggle);
        contentElem.removeEventListener('transitionend', cleanUp);
      };
    }
  }, [isEnabled, ref]);

  return (
    <Wrapper $isEnabled={isEnabled}>
      {isEnabled ? (
        <Details ref={ref}>
          <Summary>{heading}</Summary>
          <Description $isEnabled className="content">
            <HtmlContent>{content}</HtmlContent>
          </Description>
        </Details>
      ) : (
        <>
          <Heading4>{heading}</Heading4>
          <Description>
            <HtmlContent children={content} />
          </Description>
        </>
      )}
    </Wrapper>
  );
}
